import _ from 'lodash';
import * as React from 'react';

import { styled } from '@mui/material';
import * as ds from './dataService';



const ContainerDiv = styled('div')({
  flex: '1 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
});

const HiddenDiv = styled('div')(() => ({
  display: 'none'
}));



export type IFile = {
  apiFileId: string;
  dataUrl: string;
  sizeBytes: number;
  filename: string;
  fileType: string;
};

export type FileUploadFieldProps = {
  acceptTypes?: string[];
  children: (handleClick: () => void, isLoading: boolean) => JSX.Element;
  isDisabled?: boolean;
  maxFileSizeBytes?: number;
  allowDrop?: boolean;
  onUploadedFile: (file: IFile) => void;
};

export const FC = (props: FileUploadFieldProps) => {
  const [
    upload,
    uploadTuple
  ] = ds.useMutation();
  const refInput = React.useRef<HTMLInputElement>(null);
  const [
    loading,
    setLoading
  ] = React.useState(false);



  const handleClick = React.useCallback(
    () => {
      if (!props.isDisabled) {
        refInput.current?.click();
      }
    },
    [props.isDisabled]
  );



  const {
    acceptTypes,
    maxFileSizeBytes
  } = props;



  const readFileDataURL = React.useCallback(
    (file: File) => {

      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.addEventListener('load', () => {
          if (_.isString(reader.result)) {
            resolve(reader.result);
          }
        });
      });
    },
    []
  );



  const verifyAndSelectFile = React.useCallback(
    async (file: File | null) => {
      if (
        file
        && (!maxFileSizeBytes || __verifyFileSize(maxFileSizeBytes, file))
        && __verifyMimeType(acceptTypes, file)
      ) {
        setLoading(() => true);

        const [
          apiFileId,
          dataUrl
        ] = await Promise.all([
          upload(file),
          readFileDataURL(file)
        ]);

        setLoading(() => false);
        if (apiFileId) {
          props.onUploadedFile({
            apiFileId,
            filename: file.name,
            sizeBytes: file.size,
            dataUrl,
            fileType: _.last(file.name.split('.')) ?? ''
          });
        }
      }
    },
    [
      maxFileSizeBytes,
      acceptTypes,
      upload,
      readFileDataURL,
      props
    ]
  );



  const handleChange = React.useMemo(
    () => {
      const handler: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        const file = event.target.files?.item(0) ?? null;
        event.target.value = '';
        verifyAndSelectFile(file);
      };

      return handler;
    },
    [verifyAndSelectFile]
  );



  const handleDrop = React.useMemo(
    () => {
      const handler: React.DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        const item = _.first(event.dataTransfer.items);
        if (item?.kind === 'file') {
          const file = item.getAsFile();
          verifyAndSelectFile(file);
        }
      };

      return handler;
    },
    [verifyAndSelectFile]
  );



  return (
    <ContainerDiv
      onDrop={props.allowDrop ? handleDrop : undefined}
      onDragOver={props.allowDrop
        ? (event) => event.preventDefault()
        : undefined}
    >

      <HiddenDiv>
        <input
          ref={refInput}
          type='file'
          accept={props.acceptTypes
            ? _.join(props.acceptTypes, ', ')
            : undefined}
          onChange={handleChange}
          disabled={props.isDisabled || uploadTuple.loading}
        />
      </HiddenDiv>

      {props.children(handleClick, loading)}

    </ContainerDiv>
  );
};



function __verifyFileSize(maxFileSizeBytes: number, file: File) {
  if (file.size > maxFileSizeBytes) {
    window.alert(__genFileSizeExceededErrorMessage(maxFileSizeBytes));
    return false;
  }
  return true;
}



function __genFileSizeExceededErrorMessage(maxFileSizeBytes: number) {
  let size = '';
  if (maxFileSizeBytes < 1024) {
    size = `${maxFileSizeBytes} B`;
  } else if (maxFileSizeBytes < 1024 * 1024) {
    size = `${Math.ceil(maxFileSizeBytes / 1024)} KiB`;
  } else if (maxFileSizeBytes < 1024 * 1024 * 1024) {
    size = `${Math.ceil(maxFileSizeBytes / (1024 * 1024))} MiB`;
  }

  return `File size too big; cannot exceed ${size}.`;
}



function __verifyMimeType(acceptTypes: string[] | undefined, file: File) {
  if (acceptTypes && !_.includes(acceptTypes, file.type)) {
    window.alert('That file type is not allowed.');
    return false;
  }
  return true;
}
